import { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { displayName as _dn } from './lib/translit.js';
import { safeSet, safeGet } from './lib/storage.js';
import { useAuth } from './context/AuthContext.jsx';
import { useSettings } from './context/SettingsContext.jsx';
import { useUI } from './components/ToastProvider.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
// Shell-level pages stay eager (shown immediately): login, public profile, cat overlay.
import LoginPage     from './pages/LoginPage.jsx';
import LandingPage   from './pages/LandingPage.jsx';
import OnboardingFlow from './components/OnboardingFlow.jsx';
// After a deploy, chunk hashes change: an open old tab navigating to a page it
// hasn't visited yet 404s on the lazy import → white screen. Retry once, and if
// it still fails, reload once to pick up the fresh index.html (guarded so a
// truly broken build can't reload-loop).
const lazyPage = (imp) => lazy(() =>
  imp().catch(() =>
    imp().catch((err) => {
      if (!sessionStorage.getItem('chunk_reload')) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // страница уходит в reload
      }
      throw err;
    })
  ).then((mod) => { try { sessionStorage.removeItem('chunk_reload'); } catch {} return mod; })
);

const PublicProfile = lazyPage(() => import('./pages/PublicProfile.jsx'));
const CatCorner     = lazyPage(() => import('./pages/CatCorner.jsx'));
// Tab pages are code-split: each becomes its own chunk, loaded only when the tab opens.
const TodayPage     = lazyPage(() => import('./pages/TodayPage.jsx'));
const GoalsPage     = lazyPage(() => import('./pages/GoalsPage.jsx'));
const MinePage      = lazyPage(() => import('./pages/MinePage.jsx'));
const NotesPageStandalone    = lazyPage(() => import('./pages/NotesPage.jsx'));
const WishlistPageStandalone = lazyPage(() => import('./pages/WishlistPage.jsx'));
const PlayerStandalone       = lazyPage(() => import('./pages/MusicPlayer.jsx'));
const PeopleStandalone       = lazyPage(() => import('./pages/PeoplePage.jsx'));

// Hover a nav item → warm up its chunk so the switch feels instant.
const PREFETCH = {
  today:     () => import('./pages/TodayPage.jsx'),
  goals:     () => import('./pages/GoalsPage.jsx'),
  education: () => import('./pages/EducationPage.jsx'),
  markets:   () => import('./pages/WatchlistPage.jsx'),
  body:      () => import('./pages/BodyPage.jsx'),
  mine:      () => import('./pages/MinePage.jsx'),
  chat:      () => import('./pages/ChatPage.jsx'),
  profile:   () => import('./pages/ProfilePage.jsx'),
};
const prefetched = new Set();
const prefetchTab = (id) => {
  if (prefetched.has(id) || !PREFETCH[id]) return;
  prefetched.add(id);
  PREFETCH[id]().catch(() => prefetched.delete(id));
};
const EducationPage = lazyPage(() => import('./pages/EducationPage.jsx'));
const MusicPlayer   = lazyPage(() => import('./pages/MusicPlayer.jsx'));
const ProfilePage   = lazyPage(() => import('./pages/ProfilePage.jsx'));
const BodyPage      = lazyPage(() => import('./pages/BodyPage.jsx'));
const WatchlistPage = lazyPage(() => import('./pages/WatchlistPage.jsx'));
const NotesPage     = lazyPage(() => import('./pages/NotesPage.jsx'));
const WishlistPage  = lazyPage(() => import('./pages/WishlistPage.jsx'));
const PeoplePage    = lazyPage(() => import('./pages/PeoplePage.jsx'));
const ChatPage      = lazyPage(() => import('./pages/ChatPage.jsx'));
import NotificationBell from './components/NotificationBell.jsx';
import MiniPlayer from './components/MiniPlayer.jsx';
import BottomNav from './components/BottomNav.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import FocusTimerSheet from './components/FocusTimerSheet.jsx';
import ThemeSync from './components/ThemeSync.jsx';
import FocusDaysSync from './components/FocusDaysSync.jsx';
import NavIcon from './components/NavIcon.jsx';
import ThemeLoader from './components/ThemeLoader.jsx';
import { THEMES as _THEMES } from './theme/themes.js';
import { useFocusTimer } from './context/FocusTimerContext.jsx';
import { NAV, MINE_CHILDREN, FIXED_MENU, placementOf, readPlacement, writePlacement } from './data/navTabs.js';
import DraggableList from './components/DraggableList.jsx';
import { LUXE_EXTRA_CSS, glossSheen } from './lib/luxe.js';
import { track } from './analytics.js';

function useMediaQuery(q) {
  const [m, setM] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches); h();
    mq.addEventListener ? mq.addEventListener('change', h) : mq.addListener(h);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', h) : mq.removeListener(h); };
  }, [q]);
  return m;
}

export default function App() {
  const { user, token, login, logout, updateUser } = useAuth();
  // Экран инициализации: при обновлении сборки (флаг из main.jsx) и при смене темы.
  const [bootLoader, setBootLoader] = useState(() => { try { return sessionStorage.getItem('app_updating') === '1'; } catch { return false; } });
  const [switchLoader, setSwitchLoader] = useState(null); // { theme } при смене темы
  useEffect(() => {
    const onSwitch = (e) => setSwitchLoader({ theme: e.detail?.theme });
    window.addEventListener('theme_switching', onSwitch);
    return () => window.removeEventListener('theme_switching', onSwitch);
  }, []);
  const isDesktop = useMediaQuery('(min-width: 900px)');
  // Resizable sidebar: drag the right edge; width persists per device.
  const [sbWidth, setSbWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('sidebar_w'), 10);
    return Number.isFinite(v) ? Math.min(340, Math.max(190, v)) : 236;
  });
  const sbDragRef = useRef(null);
  const startSbDrag = (e) => {
    e.preventDefault();
    sbDragRef.current = { x: e.clientX, w: sbWidth };
    const move = (ev) => {
      const d = sbDragRef.current; if (!d) return;
      const w = Math.min(340, Math.max(190, d.w + (ev.clientX - d.x)));
      setSbWidth(w);
    };
    const up = () => {
      sbDragRef.current = null;
      setSbWidth((w) => { try { localStorage.setItem('sidebar_w', String(w)); } catch {} return w; });
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  const resetSbWidth = () => { setSbWidth(236); try { localStorage.setItem('sidebar_w', '236'); } catch {} };

  useEffect(() => {
    document.documentElement.style.setProperty('--content-left', isDesktop ? sbWidth + 'px' : '0px');
    if (isDesktop) document.documentElement.style.setProperty('--bottomnav-h', '0px');
  }, [isDesktop, sbWidth]);
  const headerRef = useRef(null);
  // Publish the sticky header's real height so the chat panel can pin right below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty('--appheader-h', el.offsetHeight + 'px');
    apply();
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    window.addEventListener('resize', apply);
    return () => { ro?.disconnect(); window.removeEventListener('resize', apply); };
  }, [user, isDesktop]);
  const { t, fontScale, lang, theme, localesReady } = useSettings();
  const _menuStyle = (_THEMES[theme] && _THEMES[theme].menuStyle) || {};
  const { toast } = useUI();
  const ftimer = useFocusTimer();
  const [focusOpen, setFocusOpen] = useState(false);
  // Уже входившие сразу видят форму: витрина нужна новым посетителям.
  const [showLogin, setShowLogin] = useState(() => { try { return safeGet('seen_landing', '') === '1'; } catch { return false; } });
  useEffect(() => {
    const openH = () => setFocusOpen(true);
    const doneH = (e) => { const base = t('app_focus_done', 'Фокус завершён'); toast(e && e.detail && e.detail.label ? `${base}: ${e.detail.label}` : base); };
    window.addEventListener('open_focus', openH);
    window.addEventListener('focus_done', doneH);
    return () => { window.removeEventListener('open_focus', openH); window.removeEventListener('focus_done', doneH); };
  }, [toast]);
  const VALID_TABS = ["today","goals","education","body","player","sport","health","mine","projects","profile","notes","wishlist","people","chat"];

  // If a legacy/direct link points at glossary or quiz, open the Education hub on that section.
  const [eduSection, setEduSection] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'glossary' || hash === 'quiz') return hash;
    const stored = localStorage.getItem('active_tab');
    if (stored === 'glossary' || stored === 'quiz') return stored;
    return null;
  });

  const [tab, setTab] = useState(() => {
    // 1. Try URL hash first (survives refresh)
    const hash = window.location.hash.replace('#', '');
    // Legacy redirects: old tabs → new ones; glossary/quiz now live inside Education
    const legacy = { planner: 'today', schedule: 'today', plan: 'goals', milestones: 'goals', music: 'education', glossary: 'education', quiz: 'education' };
    if (legacy[hash]) return legacy[hash];
    if (VALID_TABS.includes(hash)) return hash;
    // 2. Fall back to localStorage
    const stored = localStorage.getItem('active_tab');
    if (legacy[stored]) return legacy[stored];
    if (VALID_TABS.includes(stored)) return stored;
    return 'today';
  });

  // Tabs the user chose to hide from the nav bar (Profile → Access)
  // Межкомпонентная навигация: window-событие «открой вкладку» (например, из карточек аналитики).
  useEffect(() => {
    const h = (e) => {
      const { tab: tb, sub } = e.detail || {};
      if (!tb) return;
      if (sub) { try { setHashParam && setHashParam('sub', sub); } catch {} }
      switchTab(tb);
    };
    window.addEventListener('app_open_tab', h);
    return () => window.removeEventListener('app_open_tab', h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Онбординг первого запуска: показываем, пока не поставлен флаг.
  const [onbOpen, setOnbOpen] = useState(() => {
    try { return !localStorage.getItem('onb_done_v1'); } catch { return false; }
  });
  useEffect(() => {
    // Сервер помнит пройденный онбординг — кэш мог быть вычищен.
    if (onbOpen && user?.onboarding_done) {
      try { localStorage.setItem('onb_done_v1', '1'); } catch {}
      setOnbOpen(false);
    }
  }, [user, onbOpen]);
  const [hiddenTabs, setHiddenTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hidden_tabs') || '[]'); } catch { return []; }
  });
  // Re-read when window regains focus (so changes in Profile reflect immediately)
  useEffect(() => {
    const sync = () => {
      try { setHiddenTabs(JSON.parse(localStorage.getItem('hidden_tabs') || '[]')); } catch {}
    };
    window.addEventListener('focus', sync);
    window.addEventListener('hidden_tabs_changed', sync);
    return () => { window.removeEventListener('focus', sync); window.removeEventListener('hidden_tabs_changed', sync); };
  }, []);

  // Custom order of nav tabs (drag-to-reorder in the bar). Stored as the full ordered list of ids.
  // Placement of movable modules: 'menu' (sidebar/bottom bar) or 'mine' (hub).
  const [placement, setPlacement] = useState(readPlacement);
  // Одноразовая миграция: Рынки переехали в «Моё». Старый сохранённый placement
  // markets:'menu' перебивал новый дефолт — снимаем его один раз; последующий
  // ручной перенос в Профиле снова уважается (флаг не даёт повторить).
  useEffect(() => {
    try {
      if (localStorage.getItem('mig_markets_mine_v1')) return;
      localStorage.setItem('mig_markets_mine_v1', '1');
      const cur = readPlacement();
      if (cur.markets === 'menu') {
        delete cur.markets;
        writePlacement(cur);
        setPlacement(cur);
      }
    } catch { /* не критично */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => setPlacement(readPlacement());
    window.addEventListener('placement_changed', h);
    return () => window.removeEventListener('placement_changed', h);
  }, []);
  const placeOf = (id) => placementOf(placement, id);
  const ALL_META = useMemo(() => [...NAV, ...MINE_CHILDREN], []);

  const [tabOrder, setTabOrder] = useState(() => {
    let stored = [];
    try { stored = JSON.parse(localStorage.getItem('nav_order') || '[]'); } catch {}
    const ids = NAV.map(n => n.id);
    const ordered = stored.filter(id => ids.includes(id));
    for (const id of ids) if (!ordered.includes(id)) ordered.push(id);   // forward-compat: append new tabs
    return ordered;
  });
  useEffect(() => {
    const sync = () => { try { setTabOrder(JSON.parse(localStorage.getItem('nav_order') || '[]')); } catch {} };
    window.addEventListener('nav_order_changed', sync);
    return () => window.removeEventListener('nav_order_changed', sync);
  }, []);
  const persistOrder = (visibleIds) => {
    setTabOrder(prev => {
      const rest = prev.filter(id => !visibleIds.includes(id));
      const seen = new Set();
      const next = [...visibleIds, ...rest].filter(id => seen.has(id) ? false : (seen.add(id), true));
      try { safeSet('nav_order', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const hideTab = (id) => {
    setHiddenTabs(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try { safeSet('hidden_tabs', JSON.stringify(next)); } catch {}
      window.dispatchEvent(new Event('hidden_tabs_changed'));
      return next;
    });
  };

  // Pet chunk (~300K with lottie) shouldn't race the first paint: mount it
  // when the browser is idle (or shortly after on browsers without rIC).
  const [petReady, setPetReady] = useState(false);
  useEffect(() => {
    const go = () => setPetReady(true);
    if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2500 });
    else setTimeout(go, 1200);
  }, []);

  // Браузерная кнопка «назад»: switchTab пишет hash (history-записи создаются),
  // но без слушателя приложение на них не реагировало. Восстанавливаем вкладку из hash.
  useEffect(() => {
    const onHash = () => {
      const raw = (window.location.hash || '').replace('#', '').split('?')[0];
      const known = NAV.some((n) => n.id === raw) || MINE_CHILDREN.some((n) => n.id === raw) || ['markets', 'people', 'wishlist', 'projects'].includes(raw);
      if (raw && known) {
        setTab((cur) => (cur === raw ? cur : raw));
        try { safeSet('active_tab', raw); } catch {}
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (id, focus) => {
    track('tab_view', { tab: id });
    // Drop focus from the clicked nav button so no browser focus ring can
    // linger on it (some browsers keep the default ring after mouse clicks
    // regardless of :focus-visible support).
    try { document.activeElement?.blur?.(); } catch {}
    setTab(id);
    window.location.hash = focus ? `${id}?focus=${encodeURIComponent(focus)}` : id;
    try { safeSet('active_tab', id); } catch {}
  };

  // Palette navigation: glossary terms live inside the Education hub, so opening a
  // term result also switches that built-in section before scrolling to the term.
  const handlePaletteNav = (tabId, focus) => {
    if (tabId === 'education' && focus) setEduSection('glossary');
    switchTab(tabId, focus);
  };

  // When opening a chat with a specific user (from the Friends page).
  const [chatWith, setChatWith] = useState(null);
  const openChatWith = (username) => { setChatWith(username); switchTab('chat'); };

  // Unread chat messages → badge on the "Chats" nav item.
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    const { token: tk } = (() => { try { return { token: localStorage.getItem('auth_token') }; } catch { return { token: null }; } })();
    if (!tk) return;
    const fetchUnread = () => {
      const API = import.meta.env.VITE_API_URL || '/api';
      fetch(`${API}/chat/unread-total`, { headers: { Authorization: `Bearer ${tk}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setChatUnread(d.total || 0); }).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [tab]); // re-checks when switching tabs (e.g. leaving chat clears it)

  // Helper to read/write sub-state from hash params
  // e.g. #plan?week=2 or #profile?sub=access
  const getHashParam = (key) => {
    const hash = window.location.hash; // e.g. "#plan?week=2"
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return null;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    return params.get(key);
  };

  const setHashParam = (key, value) => {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    const base = qIdx === -1 ? hash : hash.slice(0, qIdx);
    const params = new URLSearchParams(qIdx === -1 ? '' : hash.slice(qIdx + 1));
    if (value === null) params.delete(key);
    else params.set(key, value);
    const query = params.toString();
    window.location.hash = base + (query ? '?' + query : '');
  };

  // Deep-link: when the hash carries ?focus=<id> (e.g. from the search palette),
  // scroll that item into view and briefly flash it. Pages tag their rows with
  // id="dl-<id>". Retries a few times while the destination list is still loading.
  useEffect(() => {
    const run = () => {
      const focus = getHashParam('focus');
      if (!focus) return;
      let tries = 0;
      const tick = () => {
        const el = document.getElementById('dl-' + focus);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('dl-flash');
          setTimeout(() => el.classList.remove('dl-flash'), 1800);
          setHashParam('focus', null);            // one-shot: clear so it doesn't refire
        } else if (++tries < 33) {
          setTimeout(tick, 150);
        }
      };
      setTimeout(tick, 120);
    };
    run();
    window.addEventListener('hashchange', run);
    return () => window.removeEventListener('hashchange', run);
  }, [tab]);

  const path = window.location.pathname;
  const publicUsername = path.startsWith('/@') ? path.slice(2) : null;
  // Handle OAuth callbacks
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('polar_connected')) {
      window.history.replaceState({}, '', '/');
      switchTab('sport');
    }
    if (params.get('polar_error')) {
      toast(t('app_polar_err') + params.get('polar_error'), { type:'error' });
      window.history.replaceState({}, '', '/');
    }
  }, []);

  if (publicUsername) {
    return (
      <Suspense fallback={<div style={{ padding: '48px 20px', textAlign: 'center', color: '#8b95a5', fontSize: 13 }}>{t('loading', 'Загрузка…')}</div>}>
        <PublicProfile username={publicUsername} onBack={() => window.location.href = '/'} />
      </Suspense>
    );
  }

  // Незалогиненный сначала видит витрину, а не форму. По ссылке из резюме
  // приходят люди, которым нужно понять, что это за проект, — форма входа
  // первым экраном отпугивала и их, и технических интервьюеров.
  // Выбор запоминается: вернувшийся пользователь сразу получает вход.
  // Ждём словарь: иначе на первом кадре англоязычный посетитель увидит
  // русские строки — плохое первое впечатление по ссылке из резюме.
  if (!localesReady) {
    return <div style={{ minHeight: '100vh', background: 'var(--app-bg,#0d0d10)' }} />;
  }

  if (!user) {
    return showLogin
      ? <LoginPage onLogin={login} onBack={() => setShowLogin(false)} />
      : <LandingPage onEnter={() => { setShowLogin(true); try { safeSet('seen_landing', '1'); } catch { /* ignore */ } }} />;
  }

  // Первый запуск: онбординг поверх всего (после логина).
  if (onbOpen) return <OnboardingFlow onDone={() => setOnbOpen(false)} switchTab={switchTab} />;
  // Обновление сборки — поверх всего, с текущей темой.
  if (bootLoader) return <ThemeLoader themeId={theme} label={t('boot_updating', 'Обновляю приложение')}
    onDone={() => { try { sessionStorage.removeItem('app_updating'); } catch {}; setBootLoader(false); }} />;

  // ── Luxe shell ──────────────────────────────────────────────────────────
  const SERIF = "'Cormorant Garamond', Georgia, serif";
  const iconBtn = (on) => ({ background:'transparent', border:'none', cursor:'pointer', fontSize:16, padding:0, lineHeight:1, color: on ? 'var(--app-accent,#c9a24b)' : 'var(--app-dim,#6a6a72)' });
  const badge = { position:'absolute', top:-5, right:-7, background:'var(--app-accent,#c9a24b)', color:'#0d0d10', fontSize:8, fontWeight:700, minWidth:13, height:13, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', lineHeight:1 };

  const titleFor = (id) => {
    if (id === 'sport' || id === 'health') id = 'body';
    const k = 'nav_' + id, tr = t(k);
    if (tr && tr !== k) return tr;
    const nav = NAV.find(n => n.id === id);
    return nav ? nav.label : ({ chat:t('nav_chat', 'Сообщения'), profile:t('nav_profile', 'Профиль'), people:t('nav_people', 'Друзья'), player:t('nav_player', 'Плеер'), projects:t('nav_notes', 'Заметки'), notes:t('nav_notes', 'Заметки'), wishlist:t('nav_wishlist', 'Вишлист') }[id] || id);
  };
  let dateStr = '';
  try { dateStr = new Intl.DateTimeFormat(lang || 'ru', { weekday:'short', day:'numeric', month:'long' }).format(new Date()).toUpperCase(); } catch { dateStr = ''; }

  const navList = (() => {
    const byId = Object.fromEntries(ALL_META.map(n => [n.id, n]));
    const seen = new Set();
    const ids = (tabOrder || []).filter(id => {
      if (id === 'chat') return false; // сообщения живут в шапке у колокольчика
      if (id === 'projects') return false; // идеи слиты с Заметками
      if (id === 'wishlist') return false; // вишлист живёт в Заметках
      if (id === 'people') return false; // друзья — в Профиле
      if (id === 'markets') return false; // рынки — виджет на Сегодня, не пункт меню
      if (!byId[id] || hiddenTabs.includes(id) || seen.has(id)) return false;
      if (placeOf(id) !== 'menu') return false; // отправлен в «Моё»
      seen.add(id); return true;
    });
    for (const n of ALL_META) if (n.id !== 'chat' && n.id !== 'projects' && n.id !== 'wishlist' && n.id !== 'markets' && !seen.has(n.id) && !hiddenTabs.includes(n.id) && placeOf(n.id) === 'menu') { seen.add(n.id); ids.push(n.id); }
    return ids.map(id => byId[id]).filter(Boolean);
  })();

  const avatarBtn = (size) => (
    <button onClick={() => switchTab('profile')} className="tap" aria-label={titleFor('profile')}
      style={{ background:'transparent', border:`1px solid ${tab==='profile'?'var(--app-accent,#c9a24b)':'#2a2a30'}`,
               color: tab==='profile'?'var(--app-accent,#c9a24b)':'#8a8a92', width:size, height:size, borderRadius:'50%',
               cursor:'pointer', overflow:'hidden', padding:0, display:'flex', alignItems:'center', justifyContent:'center',
               fontFamily:SERIF, fontSize:Math.round(size*0.45), flexShrink:0 }}>
      {(user.avatarUrl || user.avatar_url)
        ? <img src={user.avatarUrl || user.avatar_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : (user.firstName || user.username || 'A').slice(0,1).toUpperCase()}
    </button>
  );

  const headerActions = (
    <>
      {/* Скрытые «Сообщения» убирают и иконку из шапки — иначе тумблер в Профиле врал */}
      {!hiddenTabs.includes('chat') && (
      <button onClick={() => switchTab('chat')} className="tap" title={titleFor('chat')} style={{ ...iconBtn(tab==='chat'), position:'relative' }}>
        💬{chatUnread > 0 && <span style={badge}>{chatUnread>99?'99+':chatUnread}</span>}
      </button>
      )}
      {(ftimer.running || ftimer.paused) && (
        <button onClick={() => setFocusOpen(true)} className="tap" title={t("nav_focus", "Фокус")}
          style={{ display:'flex', alignItems:'center', gap:6, background:'transparent', border:'none', cursor:'pointer', padding:0, color:'var(--app-accent,#c9a24b)' }}>
          {ftimer.paused
            ? <span style={{ fontSize:12, lineHeight:1 }}>⏸</span>
            : <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--app-accent,#c9a24b)', boxShadow:'0 0 6px var(--app-accent,#c9a24b)', animation:'lux-floaty 2s ease-in-out infinite' }} />}
          <span style={{ fontSize:13, fontFamily:SERIF, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{(() => { const v = ftimer.swMode ? ftimer.elapsed : ftimer.remaining; return `${Math.floor(v/60)}:${String(v%60).padStart(2,'0')}`; })()}</span>
        </button>
      )}
      <NotificationBell />
    </>
  );

  const focusWidget = (
    <button onClick={() => setFocusOpen(true)} className="tap"
      style={{ position:'relative', overflow:'hidden', width:'100%', textAlign:'left', cursor:'pointer', border:'1px solid var(--app-border-gold,#2a2418)', borderRadius:16,
               padding:16, background:'var(--app-gold-card-grad,linear-gradient(160deg,#15140e,#101013))', color:'var(--app-text,#ece6d6)',
               animation: ftimer.running ? 'lux-glow 6s ease-in-out infinite' : 'none' }}>
      {ftimer.running && <div style={glossSheen} />}
      <div style={{ position:'relative', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:9, color:'var(--app-accent,#c9a24b)', letterSpacing:2, textTransform:'uppercase' }}>{ftimer.running ? `${t('nav_focus', 'Фокус')} ${t('fw_running', 'идёт')}` : t('nav_focus', 'Фокус')}</span>
        {ftimer.running
          ? <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--app-accent,#c9a24b)', boxShadow:'0 0 8px var(--app-accent,#c9a24b)', animation:'lux-floaty 2s ease-in-out infinite' }} />
          : <span style={{ fontFamily:SERIF, fontSize:22, color:'var(--app-accent,#c9a24b)' }}>▶</span>}
      </div>
      {ftimer.running && (
        <div style={{ position:'relative', fontFamily:SERIF, fontSize:32, marginTop:4, fontVariantNumeric:'tabular-nums' }}>{(() => { const v = ftimer.swMode ? ftimer.elapsed : ftimer.remaining; return `${Math.floor(v/60)}:${String(v%60).padStart(2,'0')}`; })()}</div>
      )}
      <div style={{ position:'relative', fontSize:11, color:'#8a8a92', marginTop:ftimer.running?1:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ftimer.running ? ((ftimer.goal && ftimer.goal.title) || ftimer.label || t('ft_session', 'Сессия фокуса')) : t('app_start_timer', 'Запустить таймер')}</div>
      {ftimer.running && ftimer.duration > 0 && (
        <div style={{ position:'relative', marginTop:11, height:3, background:'#2a2a30', borderRadius:2, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${ftimer.swMode ? 100 : Math.max(0,Math.min(100,(1-ftimer.remaining/ftimer.duration)*100))}%`, background:'linear-gradient(90deg,var(--app-accent,#c9a24b),var(--app-accent-soft,#e8cf8a))' }} />
        </div>
      )}
    </button>
  );

  const pageContent = (
    <ErrorBoundary resetKey={tab} label={t('eb_title', 'Что-то пошло не так')} hint={t('eb_hint', 'Эта вкладка не загрузилась. Остальное приложение работает.')} retryLabel={t('eb_retry', 'Попробовать снова')} reloadLabel={t('eb_reload', 'Перезагрузить страницу')}>
      <Suspense fallback={<div style={{ padding:'48px 20px', textAlign:'center', color:'#8a8a92', fontSize:13 }}>{t('loading', 'Загрузка…')}</div>}>
        {tab==="today"      && <TodayPage switchTab={switchTab} />}
        {tab==="goals"      && <GoalsPage />}
        {tab==="education"  && <EducationPage initialSection={eduSection} onSectionChange={setEduSection} />}
        {tab==="player"     && <MusicPlayer token={token} />}
        {(tab==="body"||tab==="sport"||tab==="health") && <BodyPage initial={tab==="sport"?"sport":tab==="health"?"health":null} />}
        {tab==="markets"    && <WatchlistPage />}
        {(tab==="mine" || (placeOf(tab)==='mine' && MINE_CHILDREN.some(c=>c.id===tab))) && <MinePage onMessage={openChatWith} initial={tab!=="mine"?tab:undefined} placement={placement} switchTab={switchTab} />}
        {placeOf('notes')==='menu' && tab==='notes' && <NotesPageStandalone />}
        {placeOf('wishlist')==='menu' && tab==='wishlist' && <WishlistPageStandalone />}
        {placeOf('player')==='menu' && tab==='player' && <PlayerStandalone token={token} />}
        {placeOf('people')==='menu' && tab==='people' && <PeopleStandalone onMessage={openChatWith} />}
        {tab==="notes"      && <NotesPage />}
        {tab==="wishlist"   && <WishlistPage />}
        {tab==="people"     && <PeoplePage onMessage={openChatWith} />}
        {tab==="chat"       && <ChatPage openWith={chatWith} getHashParam={getHashParam} setHashParam={setHashParam} key={chatWith || 'list'} />}
        {tab==="profile"    && <ProfilePage user={user} token={token} onUpdate={updateUser} onLogout={logout} getHashParam={getHashParam} setHashParam={setHashParam} onMessage={openChatWith}/>}
      </Suspense>
    </ErrorBoundary>
  );

  const LUXE_CSS = `
    /* Борд, чей компонент ничего не отрисовал (нет данных), не должен занимать
       место: пустой div всё равно получал rowGap контейнера и в ленте возникали
       необъяснимые дыры. */
    [data-sec]:empty{display:none}
    @keyframes up{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slide{from{opacity:0;transform:translateX(10px)}to{opacity:1;transform:translateX(0)}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
    @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes dlflash{0%,100%{box-shadow:0 0 0 0 transparent}25%,70%{box-shadow:0 0 0 2px var(--app-accent,#c9a24b)}}
    .dl-flash{animation:dlflash 1.8s ease;border-radius:10px}
.dash-two-col{display:flex;flex-direction:column;gap:14px}
    @media(min-width:900px){.dash-two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start}}
    .goals-grid{display:flex;flex-direction:column;gap:14px}
    @media(min-width:900px){.goals-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;align-items:start}}
    ::-webkit-scrollbar{width:7px;height:7px}::-webkit-scrollbar-thumb{background:var(--app-track,#23232a);border-radius:6px}::-webkit-scrollbar-track{background:transparent}
    .tap:active{opacity:.7}
    input,textarea{outline:none;font-family:inherit} button{font-family:inherit}
    button:focus,a:focus,[tabindex]:focus{outline:none}
    button:focus-visible,a:focus-visible,[tabindex]:focus-visible{outline:2px solid var(--app-accent,#c9a24b);outline-offset:2px;border-radius:8px}
    body,body *{font-family:var(--app-font,'Manrope',sans-serif)}
    body{font-variant-numeric:tabular-nums}
    button{transition:opacity .15s ease, background .2s ease}
  ` + LUXE_EXTRA_CSS;

  return (
    <div style={{ minHeight:'100vh', color:'var(--app-text,#ece6d6)', fontFamily:"var(--app-font,'Manrope',sans-serif)" }}>
      <style>{LUXE_CSS}</style>
      {switchLoader && <ThemeLoader themeId={switchLoader.theme || theme} minMs={900} onDone={() => setSwitchLoader(null)} />}

      {isDesktop ? (
        <div style={{ display:'flex', minHeight:'100vh' }}>
          <aside style={{ width:sbWidth, flexShrink:0, background:'var(--app-sidebar,#0a0a0d)', borderRight:'1px solid var(--app-border,#1f1f25)', padding:'22px 14px 18px', display:'flex', flexDirection:'column', height:'100vh', position:'sticky', top:0 }}>
            {/* Drag the edge to resize the menu; double-click resets to default */}
            <div onMouseDown={startSbDrag} onDoubleClick={resetSbWidth} title={t('sb_resize', 'Потяни, чтобы изменить ширину панели (двойной клик — сброс)')}
              style={{ position:'absolute', top:0, right:-4, width:9, height:'100%', cursor:'col-resize', zIndex:20 }} />
            <div style={{ display:'flex', alignItems:'center', gap:11, padding:'0 6px 20px' }}>
              {avatarBtn(42)}
              <div style={{ minWidth:0 }}>
                <div style={{ fontFamily:SERIF, fontSize:18, letterSpacing:.5, color:'var(--app-text,#ece6d6)', lineHeight:1.1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{_dn(user.firstName || user.username || '', lang)}</div>
                <div style={{ fontSize:9, color:'var(--app-dim,#6a6a72)', fontWeight:700, letterSpacing:1.5 }}>@{user.username}</div>
              </div>
            </div>
            <nav key={tab} style={{ display:'flex', flexDirection:'column', gap:3, overflowY:'auto', flex:1 }}>
              <DraggableList items={navList} gap={3} onReorder={persistOrder}
                intoIds={['mine']}
                onDropInto={(id) => { if (id === 'today' || id === 'mine') return; writePlacement({ ...(placement || {}), [id]: 'mine' }); }}
                renderItem={(n, drag) => {
                  const baseTab = (tab === 'sport' || tab === 'health') ? 'body' : tab;
                  const effTab = (baseTab !== 'mine' && placeOf(baseTab) === 'mine' && !navList.some(n => n.id === baseTab)) ? 'mine' : baseTab;
                  const on = n.id === effTab;
                  return (
                    <div data-active={on ? '1' : undefined} style={{ display:'flex', alignItems:'center',
                                  borderRadius: _menuStyle.shape==='bracket' ? 2 : (_menuStyle.shape==='soft' ? 16 : 10),
                                  border: _menuStyle.shape==='bracket' && on ? '1px solid var(--app-accent,#c9a24b)' : '1px solid transparent',
                                  boxShadow: _menuStyle.shape==='bracket' && on ? '0 0 10px color-mix(in srgb, var(--app-accent,#c9a24b) 40%, transparent)' : 'none',
                                  background: on?'linear-gradient(90deg,var(--app-accent-wash,#1a160c),#15151a)':'transparent' }}>
                      <button onClick={() => switchTab(n.id)} onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => prefetchTab(n.id)} className="tap"
                        style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', gap:11, padding:'10px 12px', borderRadius:10, cursor:'pointer', fontSize:13, textAlign:'left',
                                 background:'transparent', border:'none',
                                 fontWeight: on?600:500, color: on?'var(--app-accent,#c9a24b)':'#8a8a92' }}>
                        <span style={{ width:20, display:'flex', justifyContent:'center' }}><NavIcon id={n.id} emoji={n.icon} /></span>
                        <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', letterSpacing:_menuStyle.letterSpacing||undefined, textTransform:_menuStyle.uppercase?'uppercase':undefined }}>{_menuStyle.shape==='bracket' && on ? `[ ${titleFor(n.id)} ]` : titleFor(n.id)}</span>
                        {n.id==='chat' && chatUnread>0 && <span style={{ fontSize:10, fontWeight:700, color:'var(--app-accent,#c9a24b)', border:'1px solid color-mix(in srgb, var(--app-accent,#c9a24b) 40%, transparent)', padding:'0 7px', borderRadius:10, flexShrink:0 }}>{chatUnread>99?'99+':chatUnread}</span>}
                      </button>
                      <span {...drag} title={t('nav_reorder', 'Перетащить')}
                        style={{ ...drag.style, color:'var(--app-dimmer,#5a5a62)', padding:'8px 11px', fontSize:13, lineHeight:1, userSelect:'none', flexShrink:0 }}>⠿</span>
                    </div>
                  );
                }} />
            </nav>
            <div style={{ marginTop:14 }}>{focusWidget}</div>
            <div title={t("app_build", "Версия сборки")} style={{ marginTop:10, padding:'0 8px', fontSize:8.5, letterSpacing:.5, color:'var(--app-dimmer,#5a5a62)', opacity:.8, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              build {import.meta.env.VITE_APP_VERSION || 'dev'}
            </div>
          </aside>

          <main style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>
            <header ref={headerRef} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 30px', borderBottom:'1px solid var(--app-border,#1f1f25)', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:14, minWidth:0 }}>
                <span style={{ fontFamily:SERIF, fontSize:24, color:'var(--app-text,#ece6d6)', letterSpacing:.5, whiteSpace:'nowrap' }}>{titleFor(tab)}</span>
                <span style={{ fontSize:11, color:'var(--app-accent,#c9a24b)', fontWeight:600, letterSpacing:2.5, textTransform:'uppercase', whiteSpace:'nowrap' }}>{dateStr}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <button onClick={() => window.dispatchEvent(new Event('open_command_palette'))} className="tap" title="Ctrl/⌘ K"
                  style={{ display:'flex', alignItems:'center', gap:8, height:36, padding:'0 14px', background:'#101013', border:'1px solid var(--app-border,#1f1f25)', borderRadius:11, color:'var(--app-dim,#6a6a72)', fontSize:12, cursor:'pointer', minWidth:180 }}>
                  <span>⌕</span><span>{t('search_placeholder', 'Поиск…')}</span>
                </button>
                {headerActions}
              </div>
            </header>
            <div style={{ flex:1, overflowY:'auto', '--sticky-top':'0px', padding:'0 14px 0 20px' }}>
              <div style={{ zoom: tab === 'chat' ? 1 : fontScale }}>
                <div key={tab} style={{ animation:'fadeUp .42s cubic-bezier(.2,.7,.2,1)' }}>{pageContent}</div>
              </div>
            </div>
          </main>
        </div>
      ) : (
        <div style={{ minHeight:'100vh', paddingBottom:'var(--bottomnav-h, 70px)', '--sticky-top':'60px' }}>
          <header ref={headerRef} style={{ display:'flex', alignItems:'center', gap:9, paddingTop:'calc(env(safe-area-inset-top, 0px) + 8px)', paddingLeft:14, paddingRight:14, paddingBottom:11, borderBottom:'1px solid #1a1a20', position:'sticky', top:0, zIndex:20, background:'var(--app-card,#0a0a0d)' }}>
            {avatarBtn(36)}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:9, color:'var(--app-dim,#6a6a72)', fontWeight:700, letterSpacing:1.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{dateStr}</div>
              <div style={{ fontFamily:SERIF, fontSize:18, color:'var(--app-text,#ece6d6)', lineHeight:1.05, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{titleFor(tab)}</div>
            </div>
            <button onClick={() => window.dispatchEvent(new Event('open_command_palette'))} className="tap" title={t("app_search", "Поиск")} style={iconBtn(false)}>🔍</button>
            {headerActions}
          </header>

          <div key={tab} style={{ animation:'fadeUp .42s cubic-bezier(.2,.7,.2,1)', zoom: tab === 'chat' ? 1 : fontScale }}>{pageContent}</div>

          {tab !== 'player' && <MiniPlayer onOpen={() => switchTab('player')} />}
          <BottomNav items={ALL_META.filter(n => n.id !== 'chat' && placeOf(n.id) === 'menu')} order={tabOrder} hidden={hiddenTabs} active={(() => { const b = (tab==='sport'||tab==='health')?'body':tab; return (b!=='mine' && MINE_CHILDREN.some(c=>c.id===b) && placeOf(b)==='mine') ? 'mine' : b; })()} chatUnread={chatUnread} t={t}
            onSelect={switchTab} onReorder={persistOrder} onHide={hideTab} />
        </div>
      )}

      {petReady && <Suspense fallback={null}><CatCorner /></Suspense>}
      <ThemeSync />
      <FocusDaysSync />
      <CommandPalette onNavigate={handlePaletteNav} />
      <FocusTimerSheet open={focusOpen} onClose={() => setFocusOpen(false)} />
    </div>
  );
}
